import { useEffect, useState } from 'react';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import {
  checkApi,
  type CheckDetail,
  type CheckListFilter,
  type CheckSummary,
  type ResourceCheckSummary,
} from '../client';

/**
 * Query keys for recorded checks. The filter is part of the key so switching
 * date range or outcome refetches rather than showing a stale page.
 */
export const checkKeys = {
  all: ['check', 'checks'] as const,
  list: (organisationId: string, filter: CheckListFilter) =>
    ['check', 'checks', 'org', organisationId, filter] as const,
  byId: (id: string) => ['check', 'checks', 'id', id] as const,
  /** A photo (answerId set) or the signature (answerId null) on one check. */
  attachment: (checkId: string, answerId: string | null) =>
    ['check', 'checks', 'id', checkId, 'attachment', answerId ?? 'signature'] as const,
  resourceSummaries: (organisationId: string) =>
    ['check', 'checks', 'resource-summaries', organisationId] as const,
};

/**
 * Recorded checks, newest first. Read-only — a check is created by a driver in
 * myDriver, never from the web, so there are no mutations here.
 *
 * Short staleTime: unlike templates, this list changes throughout the day as
 * drivers submit.
 */
export function useChecks(
  organisationId: string | null,
  filter: CheckListFilter = {},
  options?: Omit<UseQueryOptions<CheckSummary[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<CheckSummary[], Error>({
    queryKey: organisationId ? checkKeys.list(organisationId, filter) : checkKeys.all,
    queryFn: () => checkApi.getChecks(organisationId!, filter),
    enabled: !!organisationId,
    staleTime: 15 * 1000,
    ...options,
  });
}

/** One check with its answers, defects and session siblings. */
export function useCheck(id: string | null, organisationId: string | null) {
  return useQuery<CheckDetail, Error>({
    queryKey: id ? checkKeys.byId(id) : checkKeys.all,
    queryFn: () => checkApi.getCheck(id!, organisationId!),
    enabled: !!id && !!organisationId,
  });
}

/**
 * The latest check per resource, keyed by resource id.
 *
 * Returns a Map rather than the raw list: every caller is the Vehicles or
 * Drivers grid asking "what about this row", which is a lookup, and building
 * the map per render for a few hundred rows is waste the hook can do once.
 *
 * Resources with no check are absent — see `checkSummaryFor` for the
 * "never checked" fallback.
 */
export function useResourceCheckSummaries(
  organisationId: string | null,
  options?: Omit<
    UseQueryOptions<ResourceCheckSummary[], Error, Map<string, ResourceCheckSummary>>,
    'queryKey' | 'queryFn' | 'enabled' | 'select'
  >,
) {
  return useQuery<ResourceCheckSummary[], Error, Map<string, ResourceCheckSummary>>({
    queryKey: organisationId
      ? checkKeys.resourceSummaries(organisationId)
      : checkKeys.all,
    queryFn: () => checkApi.getResourceCheckSummaries(organisationId!),
    enabled: !!organisationId,
    // Same window as the checks list: both move as drivers submit through
    // the day, and a grid showing yesterday's status is the bug this
    // replaced.
    staleTime: 15 * 1000,
    select: (rows) => new Map(rows.map((r) => [r.resourceId, r])),
    ...options,
  });
}

/**
 * A check attachment as an object URL an <img> can show.
 *
 * The bytes are cached by React Query; the object URL is made per mount and
 * revoked on unmount, otherwise every visit to a check with photos leaks its
 * images for the life of the tab.
 *
 * Only ever for <img>. Never navigate to the URL: a signature is an SVG, and an
 * SVG opened as a page runs any script inside it in this app's origin. In an
 * <img> it cannot.
 */
export function useCheckAttachment(checkId: string | null, answerId: string | null, enabled = true) {
  const query = useQuery<Blob, Error>({
    queryKey: checkId ? checkKeys.attachment(checkId, answerId) : checkKeys.all,
    queryFn: () =>
      answerId ? checkApi.getCheckAnswerPhoto(checkId!, answerId) : checkApi.getCheckSignature(checkId!),
    enabled: !!checkId && enabled,
    // Evidence on a submitted check never changes.
    staleTime: Infinity,
  });

  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!query.data) return;
    const next = URL.createObjectURL(query.data);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [query.data]);

  return { url, isLoading: query.isPending && !!checkId && enabled, isError: query.isError };
}
